import { useState, useEffect, useCallback } from "react";
import { RefreshCw } from "lucide-react";
import { Button } from "@/components/ui/button";

export const ServiceWorkerUpdater = () => {
  const [waitingWorker, setWaitingWorker] = useState<ServiceWorker | null>(null);
  const [showUpdate, setShowUpdate] = useState(false);

  useEffect(() => {
    if (!("serviceWorker" in navigator)) return;

    const handleUpdate = (registration: ServiceWorkerRegistration) => {
      if (registration.waiting) {
        // Auto-activate the new SW immediately (no user prompt needed)
        registration.waiting.postMessage({ type: "SKIP_WAITING" });
      }
    };

    // Check for updates on existing registration
    navigator.serviceWorker.getRegistration().then((reg) => {
      if (!reg) return;

      // Already waiting — activate immediately
      if (reg.waiting) {
        handleUpdate(reg);
      }

      // Detect new waiting worker
      reg.addEventListener("updatefound", () => {
        const newWorker = reg.installing;
        if (!newWorker) return;
        newWorker.addEventListener("statechange", () => {
          if (newWorker.state === "installed" && navigator.serviceWorker.controller) {
            // Auto-activate immediately
            newWorker.postMessage({ type: "SKIP_WAITING" });
          }
        });
      });
    });

    // When a new SW takes over, reload
    let refreshing = false;
    navigator.serviceWorker.addEventListener("controllerchange", () => {
      if (!refreshing) {
        refreshing = true;
        window.location.reload();
      }
    });

    // Periodically check for updates (every 30 min)
    const interval = setInterval(() => {
      navigator.serviceWorker.getRegistration().then((reg) => reg?.update());
    }, 30 * 60 * 1000);

    return () => clearInterval(interval);
  }, []);

  const applyUpdate = useCallback(() => {
    if (waitingWorker) {
      waitingWorker.postMessage({ type: "SKIP_WAITING" });
    }
  }, [waitingWorker]);

  if (!showUpdate) return null;

  return (
    <div className="fixed bottom-20 left-1/2 -translate-x-1/2 z-50 animate-in slide-in-from-bottom-4">
      <Button
        onClick={applyUpdate}
        size="sm"
        className="rounded-full shadow-lg gap-2 px-5"
      >
        <RefreshCw className="h-4 w-4" />
        Tap to update
      </Button>
    </div>
  );
};
